import type { ExportFormat } from './export-selection';

/** The export panel's five option categories, in display order. */
export type ExportCategoryId = 'page' | 'tableau' | 'contenu' | 'habillage' | 'apparence';

/**
 * The controls inside the shared categories that some format cannot honour.
 * Every other control of a visible category is shown for every format.
 */
export type ExportControlId =
  | 'density'
  | 'rowHeightMode'
  | 'repeatHeaders'
  | 'levierNames'
  | 'structure'
  | 'pageNumbers'
  | 'assemblyMarks'
  | 'subjectDescription';

/**
 * Which categories each format shows — export-rework chantier 4 (issue #12).
 *
 * - pdf: everything.
 * - html: no Page — an HTML file has no pages to lay out (spec R1).
 * - excel (decision X2): no Page, and no Habillage either — spec F1 rejected
 *   a « classeur document », so the sheet carries no framing.
 *
 * A full `Record` rather than a list of exceptions, so adding a format or a
 * category is a compile error until every cell is decided.
 */
const CATEGORY_VISIBILITY: Record<ExportFormat, Record<ExportCategoryId, boolean>> = {
  pdf: { page: true, tableau: true, contenu: true, habillage: true, apparence: true },
  html: { page: false, tableau: true, contenu: true, habillage: true, apparence: true },
  excel: { page: false, tableau: true, contenu: true, habillage: false, apparence: true },
};

/**
 * Which masked controls each format shows.
 *
 * - html drops the pagination-only controls: header repetition, page numbers
 *   and assembly marks (chantier 2 behaviour).
 * - excel (decision X2, and #29 for the structure) keeps « Hauteur des lignes »,
 *   « Noms des leviers » and « Structure » only in Tableau: density is cell
 *   padding, which Excel has no equivalent for; header repetition is replaced
 *   by frozen panes. The transposed structure is one table with the axes
 *   swapped (decision X3, reversed by #29), so it keeps frozen panes, filtering
 *   and copy-paste. It renders no sujet/description block either.
 *
 * Hidden is masking, not reset (spec E3): the value stays in state and each
 * renderer ignores it for the formats that hide it.
 */
const CONTROL_VISIBILITY: Record<ExportFormat, Record<ExportControlId, boolean>> = {
  pdf: {
    density: true,
    rowHeightMode: true,
    repeatHeaders: true,
    levierNames: true,
    structure: true,
    pageNumbers: true,
    assemblyMarks: true,
    subjectDescription: true,
  },
  html: {
    density: true,
    rowHeightMode: true,
    repeatHeaders: false,
    levierNames: true,
    structure: true,
    pageNumbers: false,
    assemblyMarks: false,
    subjectDescription: true,
  },
  excel: {
    density: false,
    rowHeightMode: true,
    repeatHeaders: false,
    levierNames: true,
    structure: true,
    pageNumbers: false,
    assemblyMarks: false,
    subjectDescription: false,
  },
};

/** Whether `format` shows the option category `id`. */
export function isCategoryVisible(format: ExportFormat, id: ExportCategoryId): boolean {
  return CATEGORY_VISIBILITY[format][id];
}

/** Whether `format` shows the control `id` (inside a category it shows). */
export function isControlVisible(format: ExportFormat, id: ExportControlId): boolean {
  return CONTROL_VISIBILITY[format][id];
}
